import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from "@sentry/react";
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createHashRouter, RouterProvider } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Router, RouterNavigateOptions, To } from '@remix-run/router';
import { isEqual } from 'lodash';
import { Modal } from 'react-bootstrap';

import FakeRoot from './FakeRoot';
import SelectTerritoryQuoteTypeModal from './Modals/SelectTerritoryQuoteTypeModal';
import AlertModal from './Parts/AlertModal';
import Button from './Parts/Button';
import Header from './Parts/Header/Header';
import PortalRoutes from './PortalRoutes';
import store, { UsePortalDispatch } from './Store';
import { closeCreateQuote, openCreateQuote } from './Store/ui';
import UserTimer from './userTimer';
import SpinningWheel from './Parts/SpinningWheel';
import { useQuoteTypes } from './Store/entities/quotes/hooks';
import { useTerritories } from './Store/entities/hooks';
import { useCreateQuoteOpen, usePageLoading } from './Store/ui/hooks';
import { useUser, useUserTheme } from './Store/user/hooks';
import { initialHydration } from './Store/user';
import { AlertProps } from './FakeRootType';
import { wentToAdmin } from './Store/entities/quotes';
import UnderConstruction from './UnderConstruction/Content';
import apiCalls from './PowerShadesAPIFunctions';
import extractJwtData from './extractJwtData';
import { logout } from './Store/upperLevelReducers';
import { useQuoteById } from './Store/entities/quotes/hooks';
import {
	CtaAnnouncementBanner,
	CtaAnnouncementPopup,
	CtaElementsProvider,
} from './CtaElements/CtaElementsManager';

const SESSION_WARNING_THRESHOLD_MS = 2 * 60 * 1000;

const getCurrentQuoteId = () => {
	const parseCandidate = (candidate: string) => {
		const parsed = parseInt(candidate, 10);

		return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
	};

	const querySearch = window.location.search ?? '';
	const searchParams = new URLSearchParams(querySearch);
	const searchQuoteId = parseCandidate(searchParams.get('quoteID') ?? searchParams.get('quoteId') ?? '');

	if (searchQuoteId > 0) {
		return searchQuoteId;
	}

	const hash = window.location.hash ?? '';
	const hashQueryIndex = hash.indexOf('?');

	if (hashQueryIndex >= 0) {
		const hashQuery = hash.slice(hashQueryIndex + 1);
		const hashParams = new URLSearchParams(hashQuery);
		const hashQuoteId = parseCandidate(hashParams.get('quoteID') ?? hashParams.get('quoteId') ?? '');

		if (hashQuoteId > 0) {
			return hashQuoteId;
		}
	}

	return 0;
};

const Layout = ({ reactPlugin }: { reactPlugin?: ReactPlugin }) => {
	const dispatch = UsePortalDispatch();

	const theme = useUserTheme();

	useEffect(() => {
		document.documentElement.setAttribute('data-bs-theme', theme);
	}, [theme]);

	const isCreateQuoteOpen = useCreateQuoteOpen();
	const isPageLoading = usePageLoading();
	const quoteTypes = useQuoteTypes();
	const territories = useTerritories();
	const user = useUser();
	const [activeQuoteId, setActiveQuoteId] = useState(getCurrentQuoteId);
	const quote = useQuoteById(activeQuoteId);
	const [nowMs, setNowMs] = useState(Date.now());
	const [sessionExpiryWarningOpen, setSessionExpiryWarningOpen] = useState(false);
	const [dismissedWarningJwt, setDismissedWarningJwt] = useState('');

	const windowDoc = window.document;

	const [title, setTitle] = useState(windowDoc.title);

	const defaultAlertOptions = useMemo(() => ({
		title: '',
		text: '',
		noCancelButton: false,
		acceptButtonText: '',
		cancelButtonText: '',
		onAccept: () => { /* Empty Default Function */ },
		onCancel: () => { /* Empty Default Function */ },
		quoteType: 'cedia',
		icon: '',
		smallIcon: true,
		closeOnDocumentClick: false,
		oneButton: false,
		quickFormat: '',
		active: false,
	}), []);

	useEffect(() => {
		user.authenticated === true
			&& dispatch(initialHydration());
	}, [dispatch, user.authenticated]);
	
	const [alertOptions, setAlertOptions] = useState<AlertProps & { active: boolean }>(defaultAlertOptions);

	const handleTitleChange = useCallback((workingTitle: string) => {
		const currentTitle = windowDoc.title;
		if (currentTitle === workingTitle) return;
		windowDoc.title = workingTitle;
		setTitle(workingTitle);
	}, [setTitle, windowDoc]);
	
	const alertAccept = React.useRef<() => void>(() => { /* Empty Default Function */ });
	const alertCancel = React.useRef<() => void>(() => { /* Empty Default Function */ });

	const [isPortalDisabled, setIsPortalDisabled] = useState(false);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const fakeRoot = useMemo(() => FakeRoot(store), [store]);

	const { withAlert } = fakeRoot;

	const linkStoreToAlert = useCallback(() => {
		withAlert((props: AlertProps) => new Promise((resolve, reject) => {
			if (isEqual(props, defaultAlertOptions)) return;
			setAlertOptions({ ...props, active: true });
			alertAccept.current = () => {
				props.onAccept && props.onAccept();
				setAlertOptions({ ...defaultAlertOptions });
				resolve();
			};
			alertCancel.current = () => {
				props.onCancel && props.onCancel();
				setAlertOptions({ ...defaultAlertOptions });
				reject();
			};
		}));
	}, [defaultAlertOptions, withAlert]);
 
	useEffect(() => {
		linkStoreToAlert();
	}, [linkStoreToAlert]);

	const state = store.getState;
	const router = useMemo(() =>
		createHashRouter(PortalRoutes(fakeRoot, state, reactPlugin), {}), [fakeRoot, state, reactPlugin]);

	useEffect(() => {
		const userTimer = new UserTimer(dispatch, state, (router as unknown) as Router);
		userTimer.start();

		return () => userTimer.stop();
	}, [dispatch, state, router]);

	useEffect(() => {
		const syncQuoteId = () => {
			setActiveQuoteId(getCurrentQuoteId());
		};

		syncQuoteId();

		window.addEventListener('hashchange', syncQuoteId);
		window.addEventListener('popstate', syncQuoteId);

		return () => {
			window.removeEventListener('hashchange', syncQuoteId);
			window.removeEventListener('popstate', syncQuoteId);
		};
	}, []);

	useEffect(() => {
		const intervalId = setInterval(() => {
			setNowMs(Date.now());
		}, 1000);

		return () => clearInterval(intervalId);
	}, []);

	const isOnQuoteRoute = (window.location.hash ?? '').toLowerCase().includes('quote');
	const isEditingQuote = isOnQuoteRoute && (quote?.editable ?? false);

	const handleCloseSessionExpiryWarning = () => {
		setSessionExpiryWarningOpen(false);
		setDismissedWarningJwt(user.jwt ?? '');
	};

	const handleReloginWithoutSaving = () => {
		setSessionExpiryWarningOpen(false);
		setDismissedWarningJwt(user.jwt ?? '');
		dispatch(logout());
		router.navigate('/Login');
	};

	useEffect(() => {
		const jwt = user.jwt ?? '';
		const jwtData = extractJwtData(jwt);
		const expirationMs = (jwtData?.exp ?? 0) * 1000;
		const remainingMs = expirationMs - nowMs;

		if (!user.authenticated || !isEditingQuote || !jwtData?.exp) {
			setSessionExpiryWarningOpen(false);
			return;
		}

		const withinWarningWindow = remainingMs > 0 && remainingMs <= SESSION_WARNING_THRESHOLD_MS;

		if (!withinWarningWindow) {
			return;
		}

		if (dismissedWarningJwt === jwt) {
			return;
		}

		setSessionExpiryWarningOpen(true);
	}, [dismissedWarningJwt, isEditingQuote, nowMs, user.authenticated, user.jwt]);

	const closeCreateQuoteModal = () => dispatch(closeCreateQuote());

	const navigate = useCallback((path: string) => {
		router.navigate(path);
	}, [router]);

	useEffect(() => {
		fakeRoot.withNavigate(navigate as (to: To, opts?: RouterNavigateOptions | undefined) => void);
		fakeRoot.withSetTitle(handleTitleChange as (workingTitle: string) => void);
	}, [navigate, handleTitleChange, fakeRoot]);

	/* This sets the ROOT object to window.ROOT in order to support legacy code
	  It has been quarantined with an IIFE */
	(() => {
		const anyWindow = window as any;

		anyWindow.ROOT = fakeRoot;
	})();

	const createQuote = (callback: () => void) => {
		const currentTerritories = territories || [];

		if (!currentTerritories || currentTerritories.length === 0) {
			fakeRoot.alert({
				quickFormat: 'error',
				text: 'Something went wrong loading your territories. Please try again.',
			});
			if (callback && typeof callback === 'function') callback();
			return;
		}

		// Always open the modal so the user can choose roller shades vs awnings.
		if (callback && typeof callback === 'function') callback();
		dispatch(openCreateQuote());
	};

	fakeRoot.withCreateQuote(createQuote);

	useEffect(() => {
		dispatch(wentToAdmin());
	}, [title]);

	useEffect(() => {
		apiCalls.getServerActiveStatus().then((resp) => {
			const isActive = resp?.data?.is_active ?? true;

			setIsPortalDisabled(!isActive);
		});
	}, []);

	if (isPortalDisabled) {
		return (
			<UnderConstruction />
		);
	}

	return (
		<div
			className={`${isPageLoading ? 'loading' : ''
			} ${`${process.env.ENV}`.toLowerCase()}-env`}
		>
			<ToastContainer position="bottom-right" />
			<CtaElementsProvider>
				<div className="no-print">
					<div id="header-wrapper">
						<Header
							ROOT={fakeRoot}
							title={title}
							announcementBanner={<CtaAnnouncementBanner />}
						/>
					</div>

					<CtaAnnouncementPopup />

					{isCreateQuoteOpen ? (
					<SelectTerritoryQuoteTypeModal
						territories={territories || []}
						quoteTypes={quoteTypes.filter((qt) => qt.name.toLocaleLowerCase() !== "bidspec") || []}
						onClose={closeCreateQuoteModal}
						ROOT={fakeRoot}
					/>
				) : null}
				</div>
			</CtaElementsProvider>
			{isPageLoading ? (
				<div className="local-bootstrap">
					<div className="w-100">
						<div className="text-center" style={{ marginTop: '15%' }}>
							<SpinningWheel size="5x" />
						</div>
					</div>
				</div>
			) : null}
			<div className="main-content">
				{user.loading
					? <div />
					: <RouterProvider router={router} />}
			</div>

			{alertOptions.active ? (
				<AlertModal
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...alertOptions}
					onCancel={() => alertCancel.current()}
					onAccept={() => alertAccept.current()}
				/>
			) : null}

			<Modal
				show={sessionExpiryWarningOpen}
				onHide={handleCloseSessionExpiryWarning}
				centered
				backdrop="static"
			>
				<Modal.Header closeButton>
					<Modal.Title>Session Expiring Soon</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					Your session will expire in less than 2 minutes while you are editing this quote.
					Please save your changes now.
				</Modal.Body>
				<Modal.Footer>
					<Button color="light" onClick={handleCloseSessionExpiryWarning}>
						Close
					</Button>
					<Button color="green" onClick={handleReloginWithoutSaving}>
						Re-login without saving changes
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

Layout.whyDidYouRender = {
	logOnDifferentValues: true,
	trackAllPureComponents: true,
};

export default Sentry.withProfiler(Layout);
